import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import Accordion from "react-bootstrap/Accordion";
import TaskResultsTable2 from "./TaskResultsTable2.js";
import secondsToTime from "../services/secondsToTime";
import DeleteAnswerModal from "./DeleteAnswerModal.js";
import "./ResultsHistory.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedo } from "@fortawesome/free-solid-svg-icons";

function ResultsHistory(props) {
  let studentResults = props.individualStudentResult;
  let isPrintOut = props.isPrintOut;
  let detailedView = props.detailedView;
  let studentName = props.studentName;
  let teacherid = props.teacherid;
  let schoolid = props.schoolid;
  let studentid = props.studentid;

  const [showModal, setShowModal] = useState(false);
  const [modalTask, setModalTask] = useState(null);
  const [modalAssessment, setModalAssessment] = useState(null);
  const [answerId, setAnswerId] = useState(null);
  const [assessmentResults, setAssessmentResults] = useState([]);
  const [isRetakeFullAssessment, setIsRetakeFullAssessment] = useState(false);

  const [answerIds, setAnswerIds] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("all");

  const navigate = useNavigate();

  const handleFilterChange = (e) => {
    setSelectedFilter(e.target.value);
  };

  useEffect(() => {
    if (studentResults) {
      setAssessmentResults(studentResults);
    }
  }, [studentResults]);

  // Helper function: Calculate total time
  const calculateTotalTime = (groupedTasks) =>
    groupedTasks.reduce((sum, group) => sum + group.aggregated.duration, 0);

  const calculateReadingScore = (assessment) => {
    if (
      !assessment ||
      !assessment.groupedTasks ||
      assessment.groupedTasks.length === 0
    ) {
      return 0; // Default to 0 if there are no tasks or if the assessment is not defined
    }

    // Calculate the total score directly from `assessment.groupedTasks`.
    const totalScore = assessment.groupedTasks.reduce(
      (sum, task) => sum + task.aggregated.score,
      0
    );
    const taskCount = assessment.groupedTasks.length;

    // Return the average score across all tasks.
    return Math.round((totalScore / taskCount) * 100);
  };

  // Derive properties like `totalTime` and `readingScore`
  const derivedResults = useMemo(() => {
    return assessmentResults.map((assessment) => ({
      ...assessment,
      totalTime: calculateTotalTime(assessment.groupedTasks),
      readingScore: calculateReadingScore(assessment), // Use the updated function here
    }));
  }, [assessmentResults]);

  const taskNames = useMemo(() => {
    const allTaskNames = new Set();
    derivedResults.forEach((assessment) => {
      if (assessment.groupedTasks) {
        assessment.groupedTasks.forEach((task) => {
          allTaskNames.add(task.name);
        });
      }
    });
    return Array.from(allTaskNames);
  }, [derivedResults]);

  const getReadingScoreClass = (score) => {
    if (score === 0) {
      return "low-color";
    } else if (score > 0 && score <= 20) {
      return "low-color";
    } else if (score > 20 && score <= 40) {
      return "med-color";
    } else if (score > 40 && score <= 80) {
      return "noRisk-color";
    } else if (score >= 81 && score <= 100) {
      // Include 81 in high-color
      return "high-color";
    }
    return "";
  };

  const [visibleAccordions, setVisibleAccordions] = useState(new Set());

  const showNestedData = (assessmentIndex) => {
    setVisibleAccordions((prev) => {
      const newVisible = new Set(prev);
      if (newVisible.has(assessmentIndex)) {
        newVisible.delete(assessmentIndex);
      } else {
        newVisible.add(assessmentIndex);
      }
      return newVisible;
    });
  };

  const assessmentOrder = {
    baseline: 1,
    midpoint: 2,
    "end of year": 3,
  };

  const getSortKey = (name) => {
    name = name.toLowerCase();
    if (name.includes("baseline")) return assessmentOrder["baseline"];
    if (name.includes("midpoint")) return assessmentOrder["midpoint"];
    if (name.includes("end of year")) return assessmentOrder["end of year"];
    return 4; // Default sort key for unrecognized assessments
  };

  const sortData = (field) => {
    const sortField = field === "atRisk" ? "readingScore" : field;
    setSortCriteria((prev) => {
      const isAscending = prev.field === sortField && prev.order === "asc";
      return { field: sortField, order: isAscending ? "desc" : "asc" };
    });
  };

  const [sortCriteria, setSortCriteria] = useState({
    field: null,
    order: null,
  });

  const sortedAssessmentResults = useMemo(() => {
    let sortedData = [...derivedResults];

    if (sortCriteria.field) {
      sortedData.sort((a, b) => {
        if (taskNames.includes(sortCriteria.field)) {
          const scoreA =
            a.groupedTasks.find((group) => group.name === sortCriteria.field)
              ?.aggregated.score || 0;
          const scoreB =
            b.groupedTasks.find((group) => group.name === sortCriteria.field)
              ?.aggregated.score || 0;
          return sortCriteria.order === "asc"
            ? scoreA - scoreB
            : scoreB - scoreA;
        } else if (sortCriteria.field === "readingScore") {
          return sortCriteria.order === "asc"
            ? a.readingScore - b.readingScore
            : b.readingScore - a.readingScore;
        } else if (sortCriteria.field === "name") {
          const sortKeyA = getSortKey(a.name);
          const sortKeyB = getSortKey(b.name);
          return sortCriteria.order === "asc"
            ? sortKeyA - sortKeyB
            : sortKeyB - sortKeyA;
        } else {
          return sortCriteria.order === "asc"
            ? a[sortCriteria.field] - b[sortCriteria.field]
            : b[sortCriteria.field] - a[sortCriteria.field];
        }
      });
    }

    return sortedData;
  }, [derivedResults, sortCriteria, taskNames]);

  // Sort assessments for outer accordion in descending order: baseline, midpoint, end of year
  // const orderedAssessmentResults = useMemo(() => {
  //   return [...sortedAssessmentResults].sort((a, b) => {
  //     // Lower sort key means higher priority, so sort ascending, then reverse for descending
  //     return getSortKey(a.name) - getSortKey(b.name);
  //   });
  // }, [sortedAssessmentResults]);

  const renderTaskScore = (groupedTasks, taskName) => {
    const group = groupedTasks.find((t) => t.name === taskName);
    if (!group) return "";

    const score = Math.round(group.aggregated.score * 100);
    let scoreClass = getReadingScoreClass(score);

    return <span className={`task-score ${scoreClass}`}>{score}%</span>;
  };

  // console.log(orderedAssessmentResults, "orderedAssessmentResults");

  const handleDeleteTask = useCallback(
    (event, assessmentIndex, taskIndex) => {
      event.stopPropagation();
      event.preventDefault();

      const assessment = assessmentResults[assessmentIndex];
      const task = assessment.tasks[taskIndex];
      setModalTask(task?.name);
      setModalAssessment(assessment?.name);
      setAnswerIds([task?.answerid]); 
      setShowModal(true);
      setIsRetakeFullAssessment(false);
    },
    [assessmentResults]
  );

  const handleModalClose = () => setShowModal(false);

  const handleDelete = (taskName) => {
    setAssessmentResults((prevResults) =>
      prevResults.map((assessment) => ({
        ...assessment,
        groupedTasks: assessment.groupedTasks.filter(
          (group) => group.name !== taskName
        ),
      }))
    );
  };

  const handleRTIClick = (task) => {
    navigate("/rti", {
      state: {
        studentResults: props.individualStudentResult,
        isPrintOut: props.isPrintOut,
        detailedView: props.detailedView,
        studentName: props.studentName,
        teacherid: props.teacherid,
        studentid: props.studentid,
        taskDetails: task,
      },
    });
  };

  const getSortIcon = (fieldName) => {
    if (sortCriteria.field === fieldName) {
      return sortCriteria.order === "asc"
        ? process.env.PUBLIC_URL + "/assets/imgs/desc-icon-active.png"
        : process.env.PUBLIC_URL + "/assets/imgs/asc-icon-active.png";
    }
    return process.env.PUBLIC_URL + "/assets/imgs/asc-icon-default.png";
  };

  const allEventKeys = assessmentResults.flatMap(
    (assessment, assessmentIndex) =>
      assessment.tasks.map((_, taskIndex) => `${assessmentIndex}-${taskIndex}`)
  );

  const isStaging =
    window.location.href.includes("staging") ||
    window.location.href.includes("localhost") ||
    window.location.href.includes("alpaca-assessment-test");

  const handleRetakeFullAssessment = (assessment) => {
    const allAnswerIds = assessment.tasks.map((task) => task.answerid);
    console.log("All answer IDs:", allAnswerIds);
    setModalTask("All Tasks");
    setModalAssessment(assessment.name);
    setAnswerIds(allAnswerIds);
    setShowModal(true);
    setIsRetakeFullAssessment(true);
  };

  console.log("sortedAssessmentResults:", sortedAssessmentResults);

  return (
    <>
      <Row>
        <Col>
          <div className="retake-full-assessment-button-container">
            {/* <button
              className="gold-action-button"
              onClick={handleRetakeFullAssessment}
            >
              Retake {studentName}
            </button> */}
          </div>
          <div className="">
            <div className="print-only-header">
              <thead className="table-header print-thead">
                <tr>
                  <th
                    className="table-header-cell"
                    onClick={() => sortData("name")}
                  >
                    <div className="header-content">
                      Assessment
                      <img
                        className="sort-icon"
                        src={
                          process.env.PUBLIC_URL +
                          "/assets/imgs/chevron-sort.svg"
                        }
                        alt="Sort"
                      />
                    </div>
                  </th>
                  <th
                    className="table-header-cell"
                    onClick={() => sortData("readingScore")}
                  >
                    <div className="header-content">
                      Overall Standard Score vs Test
                      <img
                        className="sort-icon"
                        src={
                          process.env.PUBLIC_URL +
                          "/assets/imgs/chevron-sort.svg"
                        }
                        alt="Sort"
                      />
                    </div>
                  </th>
                  <th
                    className="table-header-cell"
                    onClick={() => sortData("atRisk")}
                  >
                    <div className="header-content">
                      At-Risk
                      <img
                        className="sort-icon"
                        src={
                          process.env.PUBLIC_URL +
                          "/assets/imgs/chevron-sort.svg"
                        }
                        alt="Sort"
                      />
                    </div>
                  </th>
                  {taskNames.map((taskName) => (
                    <th
                      className="table-header-cell"
                      key={taskName}
                      onClick={() => sortData(taskName)}
                    >
                      <div className="header-content">
                        {taskName}
                        <img
                          className="sort-icon"
                          src={
                            process.env.PUBLIC_URL +
                            "/assets/imgs/chevron-sort.svg"
                          }
                          alt="Sort"
                        />
                      </div>
                    </th>
                  ))}
                  <th
                    className="table-header-cell"
                    onClick={() => sortData("totalTime")}
                  >
                    <div className="header-content">
                      Time
                      <img
                        className="sort-icon"
                        src={
                          process.env.PUBLIC_URL +
                          "/assets/imgs/chevron-sort.svg"
                        }
                        alt="Sort"
                      />
                    </div>
                  </th>
                  {!isPrintOut ? (
                    <th className="table-header-cell">
                      <div className="header-content">View</div>
                    </th>
                  ) : null}
                </tr>
              </thead>
            </div>
            <div className="table-responsive-wrapper">
              <Row className="mb-3 filter-row">
                <Col>
                  <div className="float-left-container">
                    <label className="label-style">Show only:</label>
                    <input
                      type="radio"
                      id="all"
                      name="filter"
                      value="all"
                      className="radio-button-style input-margin"
                      checked={selectedFilter === "all"}
                      onChange={handleFilterChange}
                    />
                    <label htmlFor="all" className="label-style">
                      All
                    </label>

                    <input
                      type="radio"
                      id="atRisk0to20"
                      name="filter"
                      value="atRisk0to20"
                      className="radio-button-style input-margin"
                      checked={selectedFilter === "atRisk0to20"}
                      onChange={handleFilterChange}
                    />
                    <label
                      htmlFor="atRisk0to20"
                      className="radio-label radio-label-red label-style"
                    >
                      At risk 0-20%
                    </label>
                    <input
                      type="radio"
                      id="atRisk21to40"
                      name="filter"
                      value="atRisk21to40"
                      className="radio-button-style input-margin"
                      checked={selectedFilter === "atRisk21to40"}
                      onChange={handleFilterChange}
                    />
                    <label
                      htmlFor="atRisk21to40"
                      className="radio-label radio-label-yellow label-style"
                    >
                      21-40%
                    </label>
                  </div>
                </Col>
              </Row>
              <Table className="custom-table">
                <thead>
                  <tr>
                    <th
                      className="table-header-cell"
                      onClick={() => sortData("name")}
                    >
                      <div className="header-content">
                        Screener
                        <img
                          className="sort-icon"
                          src={
                            process.env.PUBLIC_URL +
                            "/assets/imgs/chevron-sort.svg"
                          }
                          alt="Sort"
                        />
                      </div>
                    </th>
                    <th
                      className="table-header-cell"
                      onClick={() => sortData("readingScore")}
                    >
                      <div className="header-content">
                        Overall Standard Score vs Test
                        <img
                          className="sort-icon"
                          src={
                            process.env.PUBLIC_URL +
                            "/assets/imgs/chevron-sort.svg"
                          }
                          alt="Sort"
                        />
                      </div>
                    </th>
                    <th
                      className="table-header-cell"
                      onClick={() => sortData("atRisk")}
                    >
                      <div className="header-content">
                        At-Risk
                        <img
                          className="sort-icon"
                          src={
                            process.env.PUBLIC_URL +
                            "/assets/imgs/chevron-sort.svg"
                          }
                          alt="Sort"
                        />
                      </div>
                    </th>
                    {taskNames.map((taskName) => (
                      <th
                        className="table-header-cell"
                        key={taskName}
                        onClick={() => sortData(taskName)}
                      >
                        <div className="header-content">
                          {taskName}
                          <img
                            className="sort-icon"
                            src={
                              process.env.PUBLIC_URL +
                              "/assets/imgs/chevron-sort.svg"
                            }
                            alt="Sort"
                          />
                        </div>
                      </th>
                    ))}
                    <th
                      className="table-header-cell"
                      onClick={() => sortData("totalTime")}
                    >
                      <div className="header-content">
                        Time
                        <img
                          className="sort-icon"
                          src={
                            process.env.PUBLIC_URL +
                            "/assets/imgs/chevron-sort.svg"
                          }
                          alt="Sort"
                        />
                      </div>
                    </th>
                    {!isPrintOut ? (
                      <th className="table-header-cell">
                        <div className="header-content">View</div>
                      </th>
                    ) : null}
                  </tr>
                </thead>
                {sortedAssessmentResults
                  .filter((assessment) => {
                    if (selectedFilter === "all") return true;
                    if (selectedFilter === "atRisk0to20")
                      return assessment.readingScore <= 20;
                    if (selectedFilter === "atRisk21to40")
                      return (
                        assessment.readingScore > 20 &&
                        assessment.readingScore <= 40
                      );
                    return true;
                  })
                  .map((assessment, assessmentIndex) => (
                    <React.Fragment key={assessmentIndex}>
                      <tbody>
                        <tr
                          id={assessment.name
                            .replace(/\s+/g, "-")
                            .toLowerCase()}
                          className="table-row"
                        >
                          <td className="align-left">{assessment.name}</td>
                          <td className="centre-item task-badge-parent">
                            {assessment.readingScore ? (
                              <div className="tooltip-container">
                                <span
                                  className={`reading-score ${getReadingScoreClass(
                                    assessment.readingScore
                                  )}`}
                                >
                                  {assessment.readingScore}%
                                </span>
                                <span className="tooltip">
                                  Average of all screener Overall Standard Score vs Test this
                                  year
                                </span>
                              </div>
                            ) : (
                              <span className="not-started">Not Started</span>
                            )}
                          </td>
                          <td>
                            {assessment.readingScore !== undefined &&
                            assessment.readingScore < 20 ? (
                              <img
                                src={
                                  process.env.PUBLIC_URL +
                                  "/assets/imgs/risk-flag-red.png"
                                }
                                className="flag-size-results"
                                alt="Red Flag"
                              />
                            ) : assessment.readingScore >= 20 &&
                              assessment.readingScore <= 40 ? (
                              <img
                                src={
                                  process.env.PUBLIC_URL +
                                  "/assets/imgs/risk-flag-yellow.png"
                                }
                                className="flag-size-results"
                                alt="Yellow Flag"
                              />
                            ) : null}
                          </td>
                          {taskNames.map((taskName) => (
                            <td
                              className="results-task-score centre-item task-badge-parent"
                              data-value={taskName
                                .replace(/\s+/g, "-")
                                .toLowerCase()}
                              key={taskName}
                            >
                              {renderTaskScore(
                                assessment.groupedTasks,
                                taskName
                              )}
                            </td>
                          ))}
                          <td
                            className="results-task-score centre-item"
                            data-value={assessment.name}
                          >
                            {secondsToTime(assessment.totalTime)}
                          </td>
                          {!isPrintOut ? (
                            <td data-toggle="toggle">
                              <img
                                className="collapse-click"
                                onClick={() => showNestedData(assessmentIndex)}
                                src={
                                  visibleAccordions.has(assessmentIndex)
                                    ? "../assets/imgs/minus-icon-small.png"
                                    : "../assets/imgs/plus-icon-small.png"
                                }
                              />
                            </td>
                          ) : null}
                        </tr>
                        {visibleAccordions.has(assessmentIndex) && (
                          <tr>
                            <td colSpan={taskNames.length + 5}>
                              <Row>
                                <Col className="text-left">
                                  <div className="retake-full-assessment-button-container">
                                    <button
                                      className="gold-button"
                                      onClick={() =>
                                        handleRetakeFullAssessment(assessment)
                                      }
                                    >
                                      Retake {studentName}
                                    </button>
                                  </div>
                                </Col>
                              </Row>
                            </td>
                          </tr>
                        )}
                      </tbody>
                      {detailedView && (
                        <td
                          colSpan="12"
                          id={
                            assessment.name.replace(/\s+/g, "-").toLowerCase() +
                            "-data"
                          }
                          className={`result-accordian-width ${
                            visibleAccordions.has(assessmentIndex) || isPrintOut
                              ? ""
                              : "hide"
                          }`}
                        >
                          <Accordion
                            className="results-accordion"
                            defaultActiveKey={isPrintOut ? allEventKeys : []}
                          >
                            {assessment.tasks.map((task, taskIndex) => (
                              <Accordion.Item
                                eventKey={`${assessmentIndex}-${taskIndex}`}
                                key={`${assessmentIndex}-${taskIndex}`}
                              >
                                <Accordion.Header className="report-card-accordion-head avoid-page-break">
                                  <Row
                                    className="task-results-table-row avoid-page-break"
                                    style={{ alignItems: "center" }}
                                  >
                                    <Col
                                      // className="col-10 align-left"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <button
                                        className="delete-button"
                                        style={{ marginRight: 16 }}
                                        onClick={(e) =>
                                          handleDeleteTask(
                                            e,
                                            assessmentIndex,
                                            taskIndex
                                          )
                                        }
                                      >
                                        <FontAwesomeIcon icon={faRedo} />
                                      </button>
                                    </Col>
                                    <Col>
                                      <span
                                        style={{
                                          whiteSpace: "nowrap",
                                          // overflow: "hidden",

                                          display: "inline-block",
                                          width: "100%",
                                        }}
                                      >
                                        <i>
                                          {task.name} (
                                          {task.parentTaskName || task.name})
                                        </i>
                                      </span>
                                    </Col>
                                    {isStaging && task.score < 0.4 ? (
                                      <Col className="col-auto">
                                        <button
                                          className="rti-button"
                                          onClick={() => handleRTIClick(task)}
                                        >
                                          RTI
                                        </button>
                                      </Col>
                                    ) : (
                                      <Col className="col-auto"></Col>
                                    )}
                                  </Row>
                                </Accordion.Header>
                                <Accordion.Body>
                                  <TaskResultsTable2
                                    taskAnswers={task.answers}
                                  />
                                </Accordion.Body>
                              </Accordion.Item>
                            ))}
                          </Accordion>
                        </td>
                      )}
                    </React.Fragment>
                  ))}
              </Table>
            </div>
          </div>
        </Col>
      </Row>
      <DeleteAnswerModal
        show={showModal}
        handleClose={handleModalClose}
        taskName={modalTask}
        studentName={studentName}
        assessmentName={modalAssessment}
        handleDelete={handleDelete}
        answerIds={answerIds}
        teacherid={teacherid}
        isRetakeFullAssessment={isRetakeFullAssessment}
      />
    </>
  );
}

export default ResultsHistory;
